import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SignOut, Envelope, Phone, ChatCircleText, TrayArrowDown, CalendarCheck, CircleNotch } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { fetchStats, fetchLeads, fetchCallBookings } from "../lib/adminApi";
import { SettingsPanel, TestimonialsPanel, ThemesPanel, PressPanel } from "../components/AdminContent";
import { BRAND } from "../data/content";

const serviceLabel = { consulenza: "Consulenza", formazione: "Formazione", evento: "Evento Open Bar", generico: "Altro" };

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function StatCard({ icon: Icon, value, label, testid }) {
  return (
    <div data-testid={testid} className="bg-[#111113] border border-white/5 rounded-sm p-6 flex items-center gap-4">
      <div className="w-12 h-12 rounded-sm bg-[#D97736]/10 flex items-center justify-center text-[#D97736]">
        <Icon size={24} />
      </div>
      <div>
        <div className="font-display text-3xl font-bold">{value}</div>
        <div className="text-sm text-[#A1A1AA]">{label}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [tab, setTab] = useState("leads");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, l, c] = await Promise.all([fetchStats(), fetchLeads(), fetchCallBookings()]);
        setStats(s);
        setLeads(l);
        setCalls(c);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#09090b]">
      <header className="border-b border-white/5 bg-[#0b0b0d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-bold">
            BarMan<span className="text-[#D97736]">Prof</span>
            <span className="text-[#A1A1AA] text-sm font-sans font-normal ml-3">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-[#A1A1AA]">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 border border-white/15 text-white px-4 py-2 rounded-sm text-sm hover:border-[#D97736] hover:text-[#D97736] transition-colors">
              <SignOut size={18} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Dashboard richieste</h1>
        <p className="text-[#A1A1AA] mt-2">Preventivi e prenotazioni call ricevuti dal sito.</p>

        {loading ? (
          <div className="flex items-center gap-3 text-[#A1A1AA] mt-16 justify-center">
            <CircleNotch size={24} className="animate-spin" /> Caricamento...
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <StatCard icon={TrayArrowDown} value={stats?.leads ?? 0} label="Preventivi" testid="stat-leads" />
              <StatCard icon={CalendarCheck} value={stats?.call_bookings ?? 0} label="Prenotazioni call" testid="stat-calls" />
              <StatCard icon={ChatCircleText} value={stats?.total ?? 0} label="Totale richieste" testid="stat-total" />
            </div>

            <div className="flex flex-wrap gap-2 mt-10 mb-6 bg-[#0b0b0d] p-1.5 rounded-sm border border-white/5 w-fit">
              {[
                ["leads", `Preventivi (${leads.length})`],
                ["calls", `Call (${calls.length})`],
                ["testimonianze", "Testimonianze"],
                ["concept", "Concept Open Bar"],
                ["news", "News / Stampa"],
                ["impostazioni", "Impostazioni"],
              ].map(([key, label]) => (
                <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)} className={`px-5 py-2.5 rounded-sm text-sm font-semibold transition-all ${tab === key ? "bg-[#D97736] text-white" : "text-[#A1A1AA] hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>

            {tab === "leads" && (
              <div data-testid="leads-list" className="space-y-4">
                {leads.length === 0 && <p className="text-[#A1A1AA]">Nessun preventivo ricevuto.</p>}
                {leads.map((l) => (
                  <div key={l.id} className="bg-[#111113] border border-white/5 rounded-sm p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-lg">{l.name}</h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#A1A1AA]">
                          <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-[#D97736]"><Envelope size={15} /> {l.email}</a>
                          {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#D97736]"><Phone size={15} /> {l.phone}</a>}
                          {l.phone && <a href={`https://wa.me/${l.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#25D366] hover:opacity-80"><ChatCircleText size={15} /> WhatsApp</a>}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs bg-[#D97736]/15 text-[#D97736] px-3 py-1 rounded-sm">{serviceLabel[l.service_type] || l.service_type}</span>
                        <div className="text-xs text-[#71717A] mt-2">{fmtDate(l.created_at)}</div>
                      </div>
                    </div>
                    {l.message && <p className="mt-4 text-[#A1A1AA] text-sm leading-relaxed border-t border-white/5 pt-4">{l.message}</p>}
                  </div>
                ))}
              </div>
            )}
            {tab === "calls" && (
              <div data-testid="calls-list" className="space-y-4">
                {calls.length === 0 && <p className="text-[#A1A1AA]">Nessuna prenotazione call ricevuta.</p>}
                {calls.map((c) => (
                  <div key={c.id} className="bg-[#111113] border border-white/5 rounded-sm p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-lg">{c.name}</h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#A1A1AA]">
                          <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-[#D97736]"><Envelope size={15} /> {c.email}</a>
                          {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#D97736]"><Phone size={15} /> {c.phone}</a>}
                        </div>
                      </div>
                      <div className="text-right">
                        {(c.preferred_date || c.preferred_time) && (
                          <span className="text-xs bg-[#D97736]/15 text-[#D97736] px-3 py-1 rounded-sm inline-flex items-center gap-1.5">
                            <CalendarCheck size={13} /> {c.preferred_date} {c.preferred_time}
                          </span>
                        )}
                        <div className="text-xs text-[#71717A] mt-2">{fmtDate(c.created_at)}</div>
                      </div>
                    </div>
                    {c.topic && <p className="mt-3 text-sm text-[#F8F8F8]"><span className="text-[#A1A1AA]">Argomento:</span> {c.topic}</p>}
                    {c.message && <p className="mt-3 text-[#A1A1AA] text-sm leading-relaxed border-t border-white/5 pt-4">{c.message}</p>}
                  </div>
                ))}
              </div>
            )}
            {tab === "testimonianze" && <TestimonialsPanel />}
            {tab === "concept" && <ThemesPanel />}
            {tab === "news" && <PressPanel />}
            {tab === "impostazioni" && <SettingsPanel />}
          </>
        )}
      </main>
    </div>
  );
}
